import React, { useState, useEffect } from "react";
import useAxios from "axios-hooks";
import {
    Spinner,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    PrimaryButton,
    TextField
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import LearningObjectiveTable from "./LearningObjectiveTable";

const LearningObjectiveList = ({ crsId, revId }) => {
    const [selectedLearningObjectiveId, setSelectedLearningObjectiveId] = useState(undefined);

    const [showDeleteModal, { toggle: toggleDeleteModal }] = useBoolean(false);
    const [showCreateModal, { toggle: toggleCreateModal }] = useBoolean(false);
    const [showUpdateModal, { toggle: toggleUpdateModal }] = useBoolean(false);

    const [{ data: course, loading }, refetch] = useAxios(`courses/${crsId}/${revId || ''}`);

    if (loading)
        return <Spinner className="absolute-center" label="Loading data..." />;

    return (
        <>
            <LearningObjectiveTable
                learningObjectives={course.learningObjectives}
                setSelectedLearningObjectiveId={setSelectedLearningObjectiveId}
                selectedLearningObjectiveId={selectedLearningObjectiveId}
                createOnClick={() => toggleCreateModal()}
                deleteOnClick={() => toggleDeleteModal()}
                editOnClick={() => toggleUpdateModal()}
                loading={loading}
                createDisabled={revId && revId != course.currentCRSRevID}
            />
            <CreateLearningObjectiveModal
                courseId={crsId}
                selectedLearningObjectiveId={selectedLearningObjectiveId}
                showModal={showCreateModal}
                toggleShowModal={toggleCreateModal}
                onCreate={() => refetch()}
            />
            <UpdateLearningObjectiveModal
                courseId={crsId}
                course={course}
                selectedLearningObjectiveId={selectedLearningObjectiveId}
                showModal={showUpdateModal}
                toggleShowModal={toggleUpdateModal}
                onUpdate={() => refetch()}
            />
            <DeleteLearningObjectiveModal
                courseId={crsId}
                selectedLearningObjectiveId={selectedLearningObjectiveId}
                showModal={showDeleteModal}
                toggleShowModal={toggleDeleteModal}
                onDelete={() => refetch()}
            />
        </>
    );
};

const CreateLearningObjectiveModal = ({
    showModal,
    toggleShowModal,
    onCreate,
    courseId
}) => {
    const [learningObjective, setLearningObjective] = useState("");
    const [orderNumber, setOrderNumber] = useState("");

    const [{ loading }, update] = useAxios(
        {
            method: "post",
            url: `/courses/${courseId}/learning-objective`,
        },
        { manual: true }
    );

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Create",
                subText: "Learning Objective"
            }}
        >
            <TextField
                label="Enter the new learning objective"
                onChange={(e, newValue) => setLearningObjective(newValue)}
            />
            <TextField
                label="Enter the new order number"
                onChange={(e, newValue) => setOrderNumber(newValue)}
            />
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await update({ data: { learningObjective, orderNumber } })

                        if (!result.isAxiosError) {
                            onCreate();
                            toggleShowModal();
                        }
                    }}
                    text="Create"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

const UpdateLearningObjectiveModal = ({
    showModal,
    toggleShowModal,
    onUpdate,
    courseId,
    course,
    selectedLearningObjectiveId
}) => {
    const [learningObjective, setLearningObjective] = useState(undefined);
    const [orderNumber, setOrderNumber] = useState(undefined);

    useEffect(() => {
        setLearningObjective(course?.learningObjectives.find(l => l.crsWhatYouLearnID === selectedLearningObjectiveId)?.learningObjective);
        setOrderNumber(course?.learningObjectives.find(l => l.crsWhatYouLearnID === selectedLearningObjectiveId)?.orderNumber);
    }, [selectedLearningObjectiveId, course])

    const [{ loading }, update] = useAxios(
        {
            method: "put",
            url: `/courses/${courseId}/learning-objective/${selectedLearningObjectiveId}`,
        },
        { manual: true }
    );

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Update",
                subText: "Learning Objective"
            }}
        >
            <TextField
                label="Enter the new learning objective"
                defaultValue={learningObjective}
                onChange={(e, newValue) => setLearningObjective(newValue)}
            />
            <TextField
                label="Enter the new order number"
                defaultValue={orderNumber}
                onChange={(e, newValue) => setOrderNumber(newValue)}
            />
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await update({ data: { crsWhatYouLearnId: selectedLearningObjectiveId, learningObjective, orderNumber } });

                        if (!result.isAxiosError) {
                            onUpdate();
                            toggleShowModal();
                        }
                    }}
                    text="Update"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

const DeleteLearningObjectiveModal = ({
    showModal,
    toggleShowModal,
    onDelete,
    courseId,
    selectedLearningObjectiveId
}) => {

    const [{ loading }, deleteItem] = useAxios(
        {
            method: "delete",
            url: `/courses/${courseId}/learning-objective/${selectedLearningObjectiveId}`,
        },
        { manual: true }
    );

    return (
        <Dialog
            hidden={!showModal}
            onDismiss={toggleShowModal}
            modalProps={{
                isBlocking: true,
            }}
            dialogContentProps={{
                type: DialogType.normal,
                title: "Delete",
                subText: "Delete Learning Objective?"
            }}
        >
            <DialogFooter>
                <PrimaryButton
                    onClick={async () => {
                        const result = await deleteItem({ data: { crsWhatYouLearnId: selectedLearningObjectiveId } })

                        if (!result.isAxiosError) {
                            onDelete();
                            toggleShowModal();
                        }
                    }}
                    text="Delete"
                />
                <DefaultButton
                    loading={loading}
                    onClick={toggleShowModal}
                    text="Cancel"
                />
            </DialogFooter>
        </Dialog>
    );
};

export default LearningObjectiveList;